import { type TypographyProps, type BoxProps, Stack, type StackProps, Typography } from '@mui/material'
import { mergeSx } from 'lib/helpers/mergeSx/mergeSx'
import { SettleLogo } from 'settle-ui/components/SettleLogo/SettleLogo'

export interface OnboardingHeaderProps extends Pick<StackProps, 'sx'> {
  title: React.ReactNode
  maxWidth?: number | string
  ignoreDefaultSubtitleLayout?: boolean
  overline?: React.ReactNode
  subtitle?: React.ReactNode
  slots?: {
    logo?: React.ReactNode
  }
  slotProps?: {
    logo?: {
      sx?: BoxProps['sx']
    }
    title?: {
      sx?: TypographyProps['sx']
    }
  }
}

export const OnboardingHeader: Settle.FC<OnboardingHeaderProps> = ({
  slots,
  overline,
  title,
  subtitle,
  maxWidth,
  ignoreDefaultSubtitleLayout,
  slotProps,
  sx,
  ...props
}) => {
  const { logo } = slots ?? {}

  const renderedSubtitle = (() => {
    if (!subtitle) {
      return null
    }

    if (ignoreDefaultSubtitleLayout) {
      return subtitle
    }

    return (
      <Typography
        sx={{
          textAlign: 'center',
          color: (theme) => theme.tokens.ThemeColorCommonOnSurfaceSecondary,
        }}
      >
        {subtitle}
      </Typography>
    )
  })()

  return (
    <Stack
      {...props}
      sx={mergeSx(
        (theme) => ({
          width: '100%',
          maxWidth,
          mx: 'auto',
          alignItems: 'center',
          gap: theme.spacing(1),
        }),
        sx,
      )}
    >
      {logo ? logo : <SettleLogo sx={mergeSx({ mb: 2 }, slotProps?.logo?.sx)} />}
      {overline && (
        <Typography variant="body1" color="onSurfaceSecondary" sx={{ textAlign: 'center' }}>
          {overline}
        </Typography>
      )}
      {title && (
        <Typography
          sx={mergeSx(slotProps?.title?.sx, {
            fontSize: '27px',
            fontWeight: 700,
            lineHeight: '32px',
            textAlign: 'center',
            letterSpacing: '0',
          })}
        >
          {title}
        </Typography>
      )}
      {renderedSubtitle}
    </Stack>
  )
}
